// Facebook Import Posts Web Component
import ProtectedComponent from './protected-component.js';
import { escapeHtml } from '../utils/html-utils.js';
import { openGoogleDrivePicker } from './google-drive-picker.js';
import logger from '../utils/logger.js';
import { renderPageTitle } from '../utils/emoji-badge.js';

class FacebookImportPosts extends ProtectedComponent {
  constructor() {
    super();
    this.jobs = [];
    this.isLoading = false;
    this.isStartingImport = false;
    this.errorMessage = null;
    this.successMessage = null;
    this.pollingInterval = null;
    this.googleDriveAccounts = [];
    this.isLoadingAccounts = false;

    // Posts import state
    this.postsFileId = null;
    this.postsFileName = null;
    this.photoFileIds = [];
    this.photoFileNames = [];
    this.videoFileIds = [];
    this.videoFileNames = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
    this.attachEventListeners();
    this.loadUserAndJobs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  }

  async loadUserAndJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const authData = await window.API.request('/auth/status');
      if (!authData.authenticated) {
        throw new Error('User not authenticated');
      }

      this.currentUser = authData.user;
      await Promise.all([
        this.loadJobs(),
        this.loadGoogleDriveAccounts()
      ]);
    } catch (error) {
      logger.error('Failed to load user or jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async loadGoogleDriveAccounts() {
    try {
      if (!window.API || !this.currentUser) {
        return;
      }

      this.isLoadingAccounts = true;
      const data = await window.API.request(`/users/settings/${this.currentUser.id}/media-sources`);
      this.googleDriveAccounts = data.accounts?.['google-drive'] || [];
      this.isLoadingAccounts = false;
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to load Google Drive accounts:', error);
      this.isLoadingAccounts = false;
    }
  }

  async openFilePicker(fileType, multiselect = false) {
    if (this.googleDriveAccounts.length === 0) {
      this.errorMessage = 'Please connect a Google Drive account first in Sparkle Sources settings.';
      this.render();
      return;
    }

    const account = this.googleDriveAccounts[0];

    // Set default search query based on file type
    let defaultQuery = null;
    if (fileType === 'posts') {
      // Match: your_posts__check_ins__photos_and_videos_1.json
      defaultQuery = 'your_posts__check_ins__photos_and_videos';
    }
    // No default query for photos/videos - let users browse folders freely

    try {
      await openGoogleDrivePicker(this, 'google-drive', account.accountIdentifier, 'DOCS', (data) => {
        if (data.action === google.picker.Action.PICKED) {
          const docs = data.docs || [];

          if (multiselect) {
            // Handle multiple files - append to existing selections
            if (fileType === 'photos') {
              // Append new files, avoiding duplicates
              for (const doc of docs) {
                if (!this.photoFileIds.includes(doc.id)) {
                  this.photoFileIds.push(doc.id);
                  this.photoFileNames.push(doc.name || 'Unknown');
                }
              }
            } else if (fileType === 'videos') {
              // Append new files, avoiding duplicates
              for (const doc of docs) {
                if (!this.videoFileIds.includes(doc.id)) {
                  this.videoFileIds.push(doc.id);
                  this.videoFileNames.push(doc.name || 'Unknown');
                }
              }
            }
          } else {
            // Handle single file
            const file = docs[0];
            if (file) {
              if (fileType === 'posts') {
                this.postsFileId = file.id;
                this.postsFileName = file.name || 'Selected File';
              }
            }
          }

          this.errorMessage = null;
          this.render();
          this.attachEventListeners();
        }
      }, multiselect, defaultQuery);
    } catch (error) {
      logger.error('Failed to open file picker:', error);
      this.errorMessage = error.message || 'Failed to open Google Drive file picker. Please try again.';
      this.render();
    }
  }

  async loadJobs() {
    try {
      if (!window.API) {
        throw new Error('API not available');
      }

      const data = await window.API.request('/facebook-import/jobs');
      // Filter to only posts import jobs
      this.jobs = (data.jobs || []).filter(job => job.importType === 'posts_only');
      this.isLoading = false;
      this.render();
    } catch (error) {
      logger.error('Failed to load import jobs:', error);
      this.errorMessage = 'Failed to load import jobs';
      this.isLoading = false;
      this.render();
    }
  }

  async startImport() {
    if (!this.postsFileId) {
      this.errorMessage = 'Please select the posts JSON file';
      this.render();
      return;
    }

    // Refresh jobs list before starting to catch any stuck jobs
    await this.loadJobs();
    if (this.hasActiveJob()) {
      this.errorMessage = 'An import job is already in progress. Please wait or delete the active job.';
      this.render();
      return;
    }

    this.isStartingImport = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.render();

    try {
      const response = await window.API.request('/facebook-import/start', {
        method: 'POST',
        body: JSON.stringify({
          importType: 'posts_only',
          postsFileId: this.postsFileId,
          photoFileIds: this.photoFileIds,
          videoFileIds: this.videoFileIds
        })
      });

      this.successMessage = response.message || 'Posts import started successfully';
      // Clear form
      this.postsFileId = null;
      this.postsFileName = null;
      this.photoFileIds = [];
      this.photoFileNames = [];
      this.videoFileIds = [];
      this.videoFileNames = [];

      await this.loadJobs();
      this.startPolling();
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    } catch (error) {
      logger.error('Failed to start posts import:', error);
      this.errorMessage = error.message || 'Failed to start posts import. Please try again.';
      this.isStartingImport = false;
      this.render();
      this.attachEventListeners();
    }
  }

  startPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    this.pollingInterval = setInterval(async () => {
      const hasActiveJobs = this.jobs.some(job =>
        job.status === 'pending' || job.status === 'processing'
      );

      if (hasActiveJobs) {
        await this.loadJobs();
        this.render();
        this.attachEventListeners();
      } else {
        if (this.pollingInterval) {
          clearInterval(this.pollingInterval);
          this.pollingInterval = null;
        }
        this.render();
        this.attachEventListeners();
      }
    }, 5000);
  }

  hasActiveJob() {
    return this.jobs.some(job => job.status === 'pending' || job.status === 'processing');
  }

  async deleteJob(jobId) {
    try {
      await window.API.request(`/facebook-import/jobs/${jobId}`, {
        method: 'DELETE'
      });
      await this.loadJobs();
      this.render();
    } catch (error) {
      logger.error('Failed to delete job:', error);
      this.errorMessage = error.message || 'Failed to delete job';
      this.render();
    }
  }

  attachEventListeners() {
    // Use event delegation for better performance and to avoid issues with re-rendering
    // Only attach once using a flag
    if (!this.dataset.listenersAttached) {
      // File picker buttons and other interactive elements
      this.addEventListener('click', (e) => {
        // Find the actual button element (handles clicks on child elements like emojis)
        const pickPostsBtn = e.target.closest('#pickPostsFileBtn');
        const _pickCommentsBtn = e.target.closest('#pickCommentsFileBtn');
        const pickPhotosBtn = e.target.closest('#pickPhotosBtn');
        const pickVideosBtn = e.target.closest('#pickVideosBtn');
        const startBtn = e.target.closest('#startPostsImportBtn');
        const clearBtn = e.target.closest('.clear-file-btn');
        const deleteBtn = e.target.closest('.delete-job-btn');
        const returnBtn = e.target.closest('#returnToSettingsBtn');

        if (pickPostsBtn && !pickPostsBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.openFilePicker('posts', false);
        } else if (pickPhotosBtn && !pickPhotosBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.openFilePicker('photos', true);
        } else if (pickVideosBtn && !pickVideosBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.openFilePicker('videos', true);
        } else if (startBtn && !startBtn.disabled) {
          e.preventDefault();
          e.stopPropagation();
          this.startImport();
        } else if (clearBtn) {
          e.preventDefault();
          e.stopPropagation();
          const fileType = clearBtn.dataset.fileType;
          if (fileType === 'posts') {
            this.postsFileId = null;
            this.postsFileName = null;
          } else if (fileType === 'photos') {
            this.photoFileIds = [];
            this.photoFileNames = [];
          } else if (fileType === 'videos') {
            this.videoFileIds = [];
            this.videoFileNames = [];
          }
          this.render();
        } else {
          // Handle individual file removal
          const removeBtn = e.target.closest('.btn-remove-file');
          if (removeBtn) {
            e.preventDefault();
            e.stopPropagation();
            const fileType = removeBtn.dataset.fileType;
            const fileIndex = parseInt(removeBtn.dataset.fileIndex, 10);
            if (fileType === 'photos' && fileIndex >= 0 && fileIndex < this.photoFileIds.length) {
              this.photoFileIds.splice(fileIndex, 1);
              this.photoFileNames.splice(fileIndex, 1);
            } else if (fileType === 'videos' && fileIndex >= 0 && fileIndex < this.videoFileIds.length) {
              this.videoFileIds.splice(fileIndex, 1);
              this.videoFileNames.splice(fileIndex, 1);
            }
            this.render();
          }
        }

        if (deleteBtn) {
          e.preventDefault();
          e.stopPropagation();
          const jobId = deleteBtn.dataset.jobId;
          if (jobId && confirm('Are you sure you want to delete this import job?')) {
            this.deleteJob(jobId);
          }
        } else if (returnBtn) {
          e.preventDefault();
          e.stopPropagation();
          window.router.navigate('/settings');
        }
      });

      this.dataset.listenersAttached = 'true';
    }
  }

  render() {
    const hasActiveJobs = this.hasActiveJob();
    const hasConnectedGoogleDrive = this.googleDriveAccounts.length > 0;

    this.innerHTML = `
      <div class="facebook-import">
        <div class="settings-header">
          ${renderPageTitle('📝', 'Import Facebook Posts')}
          <p>Import your Facebook posts, comments, photos, and videos from your Facebook export</p>
        </div>

        ${this.errorMessage ? `<div class="alert alert-error">${escapeHtml(this.errorMessage)}</div>` : ''}
        ${this.successMessage ? `<div class="alert alert-success">${escapeHtml(this.successMessage)}</div>` : ''}

        ${!hasConnectedGoogleDrive ? `
          <div class="help-warning">
            ⚠️ You need to connect a Google Drive account first (if you haven't already).
            <a href="/settings/sparkle-sources">Go to Sparkle Sources settings</a> to connect.
          </div>
        ` : ''}

        <div class="import-section">
          <h3>Select Files</h3>
          <p class="section-description">Select your Facebook posts data files. The posts JSON file is required; comments, photos, and videos are optional.</p>
          
          <div class="form-group">
            <label>Posts JSON File <span class="required">*</span></label>
            ${this.postsFileId ? `
              <div class="selected-file">
                <span class="file-icon">📄</span>
                <span class="file-name">${escapeHtml(this.postsFileName || 'Selected File')}</span>
                <button type="button" class="btn-clear-file clear-file-btn" data-file-type="posts">Change</button>
              </div>
            ` : `
              <button
                type="button"
                id="pickPostsFileBtn"
                class="btn btn-secondary"
                ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
              >
                ${this.isLoadingAccounts ? 'Loading...' : '📁 Pick Posts JSON File'}
              </button>
            `}
          </div>

          <div class="form-group">
            <label>Photos <span class="optional">(optional, multiselect)</span></label>
            ${this.photoFileIds.length > 0 ? `
              <div class="selected-files">
                <details class="files-collapsible" open="false">
                  <summary class="files-summary">
                    <span class="file-icon">🖼️</span>
                    <span class="file-count">${this.photoFileIds.length} photo${this.photoFileIds.length === 1 ? '' : 's'} selected</span>
                    <span class="expand-icon">▼</span>
                  </summary>
                  <div class="files-list">
                    ${this.photoFileNames.map((name, idx) => `
                      <div class="file-item">
                        <span class="file-icon">📄</span>
                        <span class="file-name">${escapeHtml(name)}</span>
                        <button type="button" class="btn-remove-file" data-file-type="photos" data-file-index="${idx}" title="Remove this file">×</button>
                      </div>
                    `).join('')}
                  </div>
                </details>
                <div class="file-actions">
                  <button type="button" class="btn btn-secondary btn-sm" id="pickPhotosBtn" ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}>
                    ${this.isLoadingAccounts ? 'Loading...' : '➕ Add More Photos'}
                  </button>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="photos">Clear All</button>
                </div>
              </div>
            ` : `
              <button
                type="button"
                id="pickPhotosBtn"
                class="btn btn-secondary"
                ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
              >
                ${this.isLoadingAccounts ? 'Loading...' : '🖼️ Pick Photos (Select Multiple)'}
              </button>
            `}
          </div>

          <div class="form-group">
            <label>Videos <span class="optional">(optional, multiselect)</span></label>
            ${this.videoFileIds.length > 0 ? `
              <div class="selected-files">
                <details class="files-collapsible" open="false">
                  <summary class="files-summary">
                    <span class="file-icon">🎥</span>
                    <span class="file-count">${this.videoFileIds.length} video${this.videoFileIds.length === 1 ? '' : 's'} selected</span>
                    <span class="expand-icon">▼</span>
                  </summary>
                  <div class="files-list">
                    ${this.videoFileNames.map((name, idx) => `
                      <div class="file-item">
                        <span class="file-icon">📄</span>
                        <span class="file-name">${escapeHtml(name)}</span>
                        <button type="button" class="btn-remove-file" data-file-type="videos" data-file-index="${idx}" title="Remove this file">×</button>
                      </div>
                    `).join('')}
                  </div>
                </details>
                <div class="file-actions">
                  <button type="button" class="btn btn-secondary btn-sm" id="pickVideosBtn" ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}>
                    ${this.isLoadingAccounts ? 'Loading...' : '➕ Add More Videos'}
                  </button>
                  <button type="button" class="btn-clear-file clear-file-btn" data-file-type="videos">Clear All</button>
                </div>
              </div>
            ` : `
              <button
                type="button"
                id="pickVideosBtn"
                class="btn btn-secondary"
                ${this.isStartingImport || this.isLoadingAccounts ? 'disabled' : ''}
              >
                ${this.isLoadingAccounts ? 'Loading...' : '🎥 Pick Videos (Select Multiple)'}
              </button>
            `}
          </div>

          <button 
            type="button"
            id="startPostsImportBtn"
            class="btn btn-primary"
            ${this.isStartingImport || hasActiveJobs || !this.postsFileId ? 'disabled' : ''}
          >
            ${this.isStartingImport ? 'Starting Import...' : 'Start Posts Import'}
          </button>
        </div>

        ${hasActiveJobs ? '<p class="active-job-message">An import job is already in progress. Please wait or delete the active job.</p>' : ''}

        <div class="import-jobs-section">
          <h3>Your Import Jobs</h3>
          ${this.isLoading && !hasActiveJobs ? `
            <div class="loading-state">
              <div class="spinner"></div>
              <div>Loading import jobs...</div>
            </div>
          ` : this.jobs.length === 0 ? `
            <p class="empty-state">No import jobs found. Start one above!</p>
          ` : `
            <ul class="job-list">
              ${this.jobs.map(job => `
                <li class="job-item">
                  <div class="job-details">
                    <span class="job-id">#${job.id}</span>
                    <span class="job-status status-${job.status}">${escapeHtml(job.status)}</span>
                    <span class="job-created-at">${new Date(job.createdAt).toLocaleString()}</span>
                  </div>
                  <div class="job-progress">
                    <span class="progress-text">Posts: ${job.postsImported || 0}</span>
                    ${job.status === 'processing' ? '<span class="spinner spinner-small"></span>' : ''}
                  </div>
                  ${job.errorMessage ? `<div class="job-error">Error: ${escapeHtml(job.errorMessage)}</div>` : ''}
                  <div class="job-actions">
                    <button class="btn btn-sm btn-danger delete-job-btn" data-job-id="${job.id}" title="Delete job">
                      ×
                    </button>
                  </div>
                </li>
              `).join('')}
            </ul>
          `}
        </div>

        <div class="settings-footer">
          <button class="btn btn-secondary" id="returnToSettingsBtn">
            ← Return to Settings
          </button>
        </div>
      </div>
    `;
  }
}

customElements.define('facebook-import-posts', FacebookImportPosts);

